import React, { useState } from "react";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableSortEvent,
  DataTableValueArray,
} from "primereact/datatable";
import { Column } from "primereact/column";
import styled, { createGlobalStyle } from "styled-components";
import { useTranslation } from "react-i18next";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import { TypeTag } from "./typeTag";
import { SearchProduct } from "../api/models/searchProduct.models";
import { confirmDialog } from "primereact/confirmdialog";
import useDeleteAbstractProduct from "../hooks/useDeleteAbstractProduct";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import dayjs from "dayjs";
import { Family } from "../api/models/fullProductData.models";
import SearchProductExpanded from "./searchProductExpanded";
import { HttpRequestStatus, LoadingContainer } from "./loadingContainer";

const StyledTable = styled(DataTable)`
  & .edit-product-link {
    color: var(--sumi-blue);
    cursor: pointer;

    &:hover {
      color: var(--sumi-light-blue);
      text-decoration: underline;
    }
  }

  .p-datatable-tbody > tr > td {
    border: none;
    background-color: #fdfdfd;
    border-bottom: 1px solid #e1e1e1;
    padding: 1.5rem 1rem;
  }

  .p-datatable-row-expansion > tr > td {
    padding: 0 !important;
  }

  .p-datatable-thead > tr > th {
    border: none;
    font-weight: 500;
    font-size: 0.8rem;
    color: var(--sumi-table-header);
    text-transform: uppercase;
  }

  // rotate svg icon
  & .p-row-toggler svg {
    color: var(--text-color);
    transform: rotate(90deg);
  }

  // rotate 180 degrees when attribute expanded is true
  & .p-row-toggler[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }

  .p-datatable-row-expansion {
    > td {
      padding: 0 0 1rem 1rem !important;
      border: none;
      background-color: #fff;
    }

    & .p-datatable-tbody > tr > td {
      background-color: var(--sumi-background-blue);
    }

    & .p-datatable-tbody > tr > td {
      background-color: #f9f9f9 !important;
      border: none;
    }
  }
`;

const IdentifierLink = styled.span`
  color: var(--text-color);
  cursor: pointer;
  border-radius: 4px;
  white-space: nowrap;

  &:hover {
    color: var(--text-color-secondary);
    text-decoration: underline;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--sumi-blue);
  }
`;

const SearchContainer = styled.div`
  margin: 2rem 0;
  .p-inputtext {
    width: 250px;
  }
`;

const MAX_NAME_LENGTH = 55;

const ProductNameTooltipStyle = createGlobalStyle`
  .product-name-tooltip .p-tooltip-text {
    font-size: 0.85rem;
    max-width: 300px;
    white-space: normal;
    word-break: break-word;
  }
`;

interface ModelsListProps {
  searchProductsData: SearchProduct[];
  handleSelectItem: (model: SearchProduct) => void;
  totalRecords?: number;
  page?: number;
  rows?: number;
  onPageChange?: (page: number) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  isLoading?: boolean;
  sortBy: string;
  asc: boolean;
  onSortChange: (sortBy: string, asc: boolean) => void;
}

const ModelsList: React.FC<ModelsListProps> = ({
  searchProductsData,
  handleSelectItem,
  totalRecords,
  page,
  rows,
  onPageChange,
  searchValue,
  onSearchChange,
  isLoading,
  sortBy,
  asc,
  onSortChange,
}) => {
  const { t } = useTranslation();
  const [companyId] = useUIStore(
    useShallow((state: UIState) => [state.companyId]),
  );

  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  const { deleteAbstractProduct, isDeletingAbstractProduct } =
    useDeleteAbstractProduct({
      companyId: companyId,
    });

  const handleRemove = (identifier: string, name: string) => {
    confirmDialog({
      showHeader: false,
      message: t("are-you-sure-you-want-remove-product", {
        name: name,
      }),
      acceptLabel: t("cmd-remove"),
      rejectLabel: t("cmd-cancel"),
      accept: () => {
        deleteAbstractProduct(identifier);
      },
      style: { minWidth: "300px" },
    });
  };

  const createdBodyTemplate = (rowData: SearchProduct) => {
    return (
      <div style={{ whiteSpace: "nowrap" }}>
        {rowData.updated
          ? dayjs(rowData.updated).format("DD.MM.YYYY HH:mm")
          : " - "}
      </div>
    );
  };

  const nameBodyTemplate = (rowData: SearchProduct) => {
    const isTruncated = rowData.name?.length > MAX_NAME_LENGTH;
    const displayName = isTruncated
      ? `${rowData.name.slice(0, MAX_NAME_LENGTH)}...`
      : rowData.name;
    const targetClass = `product-name-${rowData.identifier}`;

    return (
      <>
        {isTruncated && (
          <Tooltip
            target={`.${targetClass}`}
            position="top"
            className="product-name-tooltip"
          />
        )}
        <IdentifierLink
          role="link"
          tabIndex={0}
          className={targetClass}
          data-pr-tooltip={isTruncated ? rowData.name : undefined}
          onClick={(e) => {
            if (e.ctrlKey || e.metaKey) {
              const url = new URL(window.location.href);
              url.searchParams.set("searchProductId", rowData.identifier);
              window.open(url.toString(), "_blank");
            } else {
              handleSelectItem(rowData);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              if (e.ctrlKey || e.metaKey) {
                const url = new URL(window.location.href);
                url.searchParams.set("searchProductId", rowData.identifier);
                window.open(url.toString(), "_blank");
              } else {
                handleSelectItem(rowData);
              }
            }
          }}
        >
          {displayName}
        </IdentifierLink>
      </>
    );
  };

  const eanBodyTemplate = (rowData: SearchProduct) => {
    return (
      <IdentifierLink
        role="link"
        tabIndex={0}
        onClick={(e) => {
          if (e.ctrlKey || e.metaKey) {
            const url = new URL(window.location.href);
            url.searchParams.set("searchProductId", rowData.identifier);
            window.open(url.toString(), "_blank");
          } else {
            handleSelectItem(rowData);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            if (e.ctrlKey || e.metaKey) {
              const url = new URL(window.location.href);
              url.searchParams.set("searchProductId", rowData.identifier);
              window.open(url.toString(), "_blank");
            } else {
              handleSelectItem(rowData);
            }
          }
        }}
      >
        {rowData.ean}
      </IdentifierLink>
    );
  };

  const actionsBodyTemplate = (rowData: SearchProduct) => {
    return (
      <div
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <span
          role="button"
          tabIndex={0}
          className="edit-product-link"
          onClick={(e) => {
            if (e.ctrlKey || e.metaKey) {
              const url = new URL(window.location.href);
              url.searchParams.set("searchProductId", rowData.identifier);
              window.open(url.toString(), "_blank");
            } else {
              handleSelectItem(rowData);
            }
          }}
        >
          {t("cmd-open-product")}
        </span>
        <span>
          <span
            role="button"
            tabIndex={0}
            style={{ marginLeft: "1rem", cursor: "pointer" }}
            onClick={() => {
              // Implement delete logic here
              handleRemove(rowData.identifier, rowData.name);
            }}
          >
            {t("cmd-delete-product")}
          </span>
        </span>
      </div>
    );
  };

  const typeTemplate = (rowData: SearchProduct) => {
    return (
      <TypeTag type={rowData.family || null}>
        {rowData.family === "product_model"
          ? t("label-model")
          : rowData.family === "package"
            ? t("label-multipliable-package")
            : t("label-product")}
      </TypeTag>
    );
  };

  const allowExpansion = (rowData: SearchProduct) => {
    return rowData.family === Family.product_model;
  };

  const rowExpansionTemplate = (data: SearchProduct) => {
    return (
      <SearchProductExpanded
        searchProduct={data}
        handleSelectItem={handleSelectItem}
      />
    );
  };

  const handleSort = (event: DataTableSortEvent) => {
    const sortField = event.sortField;
    const sortOrder = event.sortOrder;

    if (!sortField || !sortOrder) {
      return;
    }

    onSortChange(sortField, sortOrder === 1);
  };

  return (
    <div>
      <ProductNameTooltipStyle />
      <SearchContainer
        style={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("label-search")}
          />
        </IconField>
      </SearchContainer>

      <br />
      <LoadingContainer
        status={
          isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS
        }
      >
        <StyledTable
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={rowExpansionTemplate}
          value={searchProductsData}
          paginator={!searchValue}
          lazy
          rows={searchValue ? undefined : rows}
          first={searchValue || !page || !rows ? undefined : (page - 1) * rows}
          onPage={
            searchValue || !rows || !onPageChange
              ? undefined
              : (e) => onPageChange(Math.floor(e.first / rows) + 1)
          }
          totalRecords={searchValue ? undefined : totalRecords}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
          currentPageReportTemplate={t("label-pagination-report")}
          style={{ marginBottom: "2rem" }}
          sortField={sortBy}
          sortOrder={asc ? 1 : -1}
          onSort={handleSort}
          emptyMessage={t("label-no-models")}
        >
          <Column style={{ width: "65px" }} expander={allowExpansion} />
          <Column
            field="type"
            sortable
            header={t("label-type")}
            body={typeTemplate}
            style={{ width: "150px" }}
          />
          <Column
            field="name"
            sortable
            header={t("label-name")}
            body={nameBodyTemplate}
            style={{ minWidth: "350px", width: "350px" }}
          />
          <Column
            field="ean"
            sortable
            header={t("label-ean")}
            body={eanBodyTemplate}
            style={{ minWidth: "175px", width: "175px" }}
          />
          <Column
            field="internalReference"
            sortable
            header={t("label-internal-reference")}
            style={{ minWidth: "175px", width: "175px" }}
          />
          <Column
            sortable
            field="created"
            header={t("label-created")}
            body={createdBodyTemplate}
          />
          <Column field="actions" body={actionsBodyTemplate} />
        </StyledTable>
      </LoadingContainer>
    </div>
  );
};

export default ModelsList;
